import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  CircularProgress,
} from '@mui/material';

import { useApi } from '../../hooks/useApi';
import { Agent } from '../../types/agents';

interface IEditAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent | null;
  onSuccess: () => void;
}

export const EditAgentModal = ({ isOpen, onClose, agent, onSuccess }: IEditAgentModalProps) => {
  const [name, setName] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { apiFetch } = useApi();

  useEffect(() => {
    if (agent) {
      setName(agent.name || '');
      setEndpoint(agent.endpoint || '');
      setApiKey(''); // Don't pre-fill API key for security
    }
  }, [agent]);

  const handleSubmit = async () => {
    if (!name.trim() || !endpoint.trim()) {
      toast.error('Name and endpoint are required', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const body: Record<string, string> = { name, endpoint };
      if (apiKey.trim()) {
        body.key = apiKey;
      }

      const res = await apiFetch(`agent/${agent?.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        toast.success('Agent updated successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        onSuccess();
        onClose();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to update agent', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (e) {
      toast.error('Something went wrong', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600, color: '#2C3E50' }}>
        Edit Agent
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} pt={1}>
          <TextField
            label="Agent Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            variant="outlined"
            disabled={isLoading}
          />
          <TextField
            label="CXCortex Endpoint URL"
            fullWidth
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            variant="outlined"
            placeholder="https://agents.cxcortex.ai/api/v1/prediction/..."
            disabled={isLoading}
            helperText="The CXCortex prediction API URL for this agent"
          />
          <TextField
            label="API Key (optional)"
            fullWidth
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            variant="outlined"
            type="password"
            disabled={isLoading}
            helperText="Leave blank to keep existing key, or enter a new key to update"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            borderColor: '#E5E7EB',
            color: '#374151',
            '&:hover': {
              borderColor: '#D1D5DB',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          sx={{
            backgroundColor: '#0099CC',
            '&:hover': {
              backgroundColor: '#007AA3',
            },
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
